import React, { useState, useEffect, useContext, useCallback } from 'react';
import Layout from '../../components/layouts/Main';
import { AuthContext } from '../../context/AuthContext';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTimes, FaPlus, FaCheck, FaSpinner } from 'react-icons/fa';
import { Card, Row, Col, Form, Button, Badge, Table } from 'react-bootstrap';
import config from '../../config';
import axios from 'axios';

const StockTransferInstanceDetails = () => {
  const { id } = useParams();
  const { loggedUser: user } = useContext(AuthContext);
  const username = user?.username;
  const domainName = username;
  const apiUrl =  config.API_BASE_URL;

  const [instance, setInstance] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packingMaterials, setPackingMaterials] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [outputEntries, setOutputEntries] = useState([]);

  const [formData, setFormData] = useState({
    destinationWarehouse: '',
    destinationPackingMaterial: '',
    transferQuantity: '',
    numberOfBags: '',
    quantityPerBag: 0,
    remarks: ''
  });

  // Fetch output entries
  const fetchOutputEntries = useCallback(async (entryIds) => {
    try {
      const response = await axios.post(`${apiUrl}/stock-transfer-instances/entries-by-ids`, { entryIds }, {
        headers: {
          'Content-Type': 'application/json',
          'X-Subdomain': domainName
        },
        withCredentials: true
      });

      if (response.data.success) {
        setOutputEntries(response.data.entries || []);
      }
    } catch (error) {
      console.error('Error fetching output entries:', error);
    }
  }, [apiUrl, domainName]);

  // Fetch warehouses
  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/new-warehouses/storage`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Subdomain': domainName
        },
        withCredentials: true
      });

      setWarehouses(response.data || []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  }, [apiUrl, domainName]);

  // Fetch instance details
  useEffect(() => {
    const fetchInstanceDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/stock-transfer-instances/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            'X-Subdomain': domainName
          },
          withCredentials: true
        });

        const data = response.data;
        if (data.success) {
          setInstance(data.instance);
          setTransfers(data.transfers || []);

          // Fetch output entries if transfers exist
          if (data.transfers && data.transfers.length > 0) {
            const outputIds = data.transfers
              .filter(t => t.inEntryId)
              .map(t => t.inEntryId);

            if (outputIds.length > 0) {
              fetchOutputEntries(outputIds);
            }
          }
        } else {
          toast.error(data.message || 'Failed to fetch instance details');
        }
      } catch (error) {
        console.error('Error fetching instance details:', error);
        toast.error('Failed to load instance details');
      } finally {
        setLoading(false);
      }
    };

    fetchInstanceDetails();
    fetchWarehouses();
  }, [apiUrl, domainName, id, fetchOutputEntries, fetchWarehouses]);

  // Fetch packing materials when instance is loaded
  useEffect(() => {
    const fetchPackingMaterials = async () => {
      if (!instance || !instance.product) return;

      try {
        const response = await axios.get(
          `${apiUrl}/production-batches/packing-materials/product/${instance.product._id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Subdomain': domainName
            },
            withCredentials: true
          }
        );

          setPackingMaterials(response.data || []);
        
      } catch (error) {
        console.error('Error fetching packing materials:', error);
      }
    };

    if (instance) {
      fetchPackingMaterials();
    }
  }, [apiUrl, domainName, instance]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Auto-calculate number of bags
    if (name === 'transferQuantity' || name === 'destinationPackingMaterial') {
      if (formData.destinationPackingMaterial && name === 'transferQuantity') {
        const packingMaterial = packingMaterials.find(pm => pm._id === formData.destinationPackingMaterial);
        if (packingMaterial && packingMaterial.quantityPerBag) {
          const bags = Math.ceil(parseFloat(value) / packingMaterial.quantityPerBag);
          setFormData(prev => ({ ...prev, numberOfBags: bags }));
          setFormData(prev => ({ ...prev, quantityPerBag: packingMaterial.quantityPerBag }));
        }
      } else if (name === 'destinationPackingMaterial' && formData.transferQuantity) {
        const packingMaterial = packingMaterials.find(pm => pm._id === value);
        if (packingMaterial && packingMaterial.quantityPerBag) {
          const bags = Math.ceil(parseFloat(formData.transferQuantity) / packingMaterial.quantityPerBag);
          setFormData(prev => ({ ...prev, numberOfBags: bags }));
          setFormData(prev => ({ ...prev, quantityPerBag: packingMaterial.quantityPerBag }));
        }
      }
    }
  };

  const validateForm = () => {
    if (!formData.destinationWarehouse) return 'Destination warehouse is required';
    if (!formData.transferQuantity || parseFloat(formData.transferQuantity) <= 0) 
      return 'Valid transfer quantity is required';
    
    const remainingQuantity = instance ? 
      instance.quantity - transfers.reduce((sum, t) => sum + t.transferQuantity, 0) : 0;
    
    if (parseFloat(formData.transferQuantity) > remainingQuantity)
      return `Transfer quantity exceeds remaining quantity (${remainingQuantity})`;
    
    if (formData.destinationPackingMaterial && (!formData.numberOfBags || parseInt(formData.numberOfBags) <= 0))
      return 'Valid number of bags is required';
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(`${apiUrl}/stock-transfer-instances/${id}/transfers`, formData, {
        headers: {
          'Content-Type': 'application/json',
          'X-Subdomain': domainName
        },
        withCredentials: true
      });

      if (response.data.success) {
        toast.success('Transfer created successfully');
        // Refresh data
        window.location.reload();
      } else {
        toast.error(response.data.message || 'Failed to create transfer');
      }
    } catch (error) {
      console.error('Error creating transfer:', error);
      toast.error('Failed to create transfer');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return <Badge bg="primary">New</Badge>;
      case 'running':
        return <Badge bg="warning">Running</Badge>;
      case 'closed':
        return <Badge bg="success">Closed</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
          <FaSpinner className="fa-spin me-2" />
          <span>Loading...</span>
        </div>
      </Layout>
    );
  }

  if (!instance) {
    return (
      <Layout>
        <div className="alert alert-danger">
          Stock Transfer Instance not found
        </div>
      </Layout>
    );
  }

  const remainingQuantity = instance.quantity - transfers.reduce((sum, t) => sum + t.quantity, 0);
  const isCompleted = remainingQuantity <= 0 || instance.status === 'closed';

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Stock Transfer Details</h2>
        <Link to="/stock-transfer-instances" className="btn btn-outline-primary">
          <FaTimes className="me-2" /> Back to List
        </Link>
      </div>

      {/* Instance Information */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Input Information</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>Product:</strong> {instance.product?.productName}</p>
              <p><strong>Source Warehouse:</strong> {instance.sourceWarehouse?.label}</p>
              <p><strong>Batch Number:</strong> {instance.batchNumber || 'N/A'}</p>
            </Col>
            <Col md={6}>
              <p><strong>Total Quantity:</strong> {instance.quantity} {instance.unit}</p>
              <p><strong>Remaining Quantity:</strong> {remainingQuantity} {instance.unit}</p>
              <p><strong>Status:</strong> {getStatusBadge(instance.status)}</p>
            </Col>
          </Row>
          {instance.remarks && (
            <Row>
              <Col>
                <p><strong>Remarks:</strong> {instance.remarks}</p>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Create New Transfer Form */}
      {!isCompleted && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Create New Transfer</h5>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Destination Warehouse</Form.Label>
                    <Form.Select
                      name="destinationWarehouse"
                      value={formData.destinationWarehouse}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Destination Warehouse</option>
                      {warehouses.map(warehouse => (
                        <option key={warehouse._id} value={warehouse._id}>
                          {warehouse.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Transfer Quantity ({instance.unit})</Form.Label>
                    <Form.Control
                      type="number"
                      name="transferQuantity"
                      value={formData.transferQuantity}
                      onChange={handleInputChange}
                      min="0.01"
                      max={remainingQuantity}
                      step="0.01"
                      required
                    />
                    <Form.Text className="text-muted">
                      Available: {remainingQuantity} {instance.unit}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Packing Material</Form.Label>
                    <Form.Select
                      name="destinationPackingMaterial"
                      value={formData.destinationPackingMaterial}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Packing Material (Optional)</option>
                      {packingMaterials.map(pm => (
                        <option key={pm._id} value={pm._id}>
                          {pm.name} ({pm.quantityPerBag} {instance.unit}/bag)
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Number of Bags</Form.Label>
                    <Form.Control
                      type="number"
                      name="numberOfBags"
                      value={formData.numberOfBags}
                      onChange={handleInputChange}
                      min="1"
                      step="1"
                      disabled={!formData.destinationPackingMaterial}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Remarks</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      rows={2}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex justify-content-end">
                <Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? (
                    <>
                      <FaSpinner className="fa-spin me-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaPlus className="me-2" />
                      Create Transfer
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      {/* Transfers List */}
      <Card className="mb-4 d-none">
        <Card.Header>
          <h5 className="mb-0">Transfers</h5>
        </Card.Header>
        <Card.Body>
          {transfers.length === 0 ? (
            <div className="alert alert-info">No transfers created yet</div>
          ) : (
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>Destination</th>
                  <th>Quantity</th>
                  <th>Packing Material</th>
                  <th>Bags</th>
                  <th>Date</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map(transfer => (
                  <tr key={transfer._id}>
                    <td>{transfer.destinationWarehouse?.label}</td>
                    <td>{transfer.quantity} {instance.unit}</td>
                    <td>{transfer.destinationPackingMaterial?.name || 'N/A'}</td>
                    <td>{transfer.numberOfBags || 'N/A'}</td>
                    <td>{new Date(transfer.createdAt).toLocaleString()}</td>
                    <td>{transfer.remarks || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* New Stock Entries (Output) Section */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">New Stock Entries (Output)</h5>
        </Card.Header>
        <Card.Body>
          {outputEntries.length === 0 ? (
            <div className="alert alert-info">No output entries created yet</div>
          ) : (
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>Warehouse</th>
                  <th>Quantity</th>
                  <th>Packing Material</th>
                  <th>Bags</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {outputEntries.map(entry => (
                  <tr key={entry._id}>
                    <td>{entry.warehouseLocationId?.label}</td>
                    <td>{entry.quantity} {entry.unit}</td>
                    <td>
                      <p className="mb-0">{entry.packingMaterialId?.name || 'N/A'}</p>
                      <small className="text-muted">{entry.packingMaterialId.size || ''}</small></td>
                    <td>{entry.numberOfBags || 'N/A'}</td>
                    <td>{new Date(entry.createdAt).toLocaleString()}</td>
                    <td>
                      <Badge bg="success">
                        <FaCheck className="me-1" /> Created
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Layout>
  );
};

export default StockTransferInstanceDetails;
